<!-- components/SmartImageCard.vue -->
<template>
  <div class="smart-card-wrapper">
    <!-- 循环渲染每一条数据 -->
    <div 
      v-for="(item, index) in items" 
      :key="item.id || index" 
      class="smart-card-item"
    >
      <!-- 1. 图片区域 -->
      <div class="image-box" @click="handleImageClick(item)">
        <img :src="item.url" :alt="item.alt || 'Image'" class="card-img" />
        <div class="img-mask">
          <span class="mask-text">🔍 点击放大</span>
        </div>
      </div>

      <!-- 2. 操作区域 (动态渲染) -->
      <div class="action-box">
        <template v-for="(act, aIndex) in getVisibleActions(item)" :key="aIndex">
          
          <!-- 类型：按钮 -->
          <button 
            v-if="act.type === 'button'" 
            class="act-btn"
            :style="[defaultBtnStyle, act.style]"
            @click.stop="handleAction(act, item, index)"
          >
            {{ typeof act.label === 'function' ? act.label(item) : act.label }}
          </button>

          <!-- 类型：文本/Div -->
          <div 
            v-else-if="act.type === 'text' || act.type === 'div'" 
            class="act-text"
            :style="[defaultTextStyle, act.style]"
            @click.stop="handleAction(act, item, index)"
          >
            {{ typeof act.label === 'function' ? act.label(item) : act.label }}
          </div>

        </template>
      </div>
    </div>

    <!-- 3. 内置图片放大预览 (模仿 Modal 结构) -->
    <teleport to="body">
      <div v-if="previewVisible" class="preview-mask">
        <div class="preview-container">
          <div class="preview-top">
            <div class="preview-title">图片预览</div>
            <div class="preview-close" @click="closePreview">x</div>
          </div>
          <div class="preview-body">
            <img :src="currentPreviewUrl" class="preview-img-full" />
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
export default {
  name: 'SmartImageCard',
  props: {
    // 展示数据 [{ url: '', id: 1, ... }]
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    // 配置数据 { cardWidth, actions: [...] }
    /*
    actions: [
          {
            type: 'button',
            label: '通过',
            condition: (item) => item.status === 'wait',
            style: { backgroundColor: '#67C23A' },
            action: (item) => { console.log('通过', item); }
          },
          {
            type: 'text',
            label: '已审核',
            condition: (item) => item.status === 'pass',
            style: { color: '#999' },
            action: (item) => { console.log('查看', item); }
          }
        ]*/
    config: {
      type: Object,
      default: () => ({
        cardWidth: '280px',
        cardGap: '16px',
        imageHeight: '200px',
        actions: []
      })
    }
  },
  data() {
    return {
      previewVisible: false,
      currentPreviewUrl: '',
      defaultBtnStyle: {
        padding: '6px 12px',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        marginRight: '8px',
        backgroundColor: '#409EFF',
        color: '#fff'
      },
      defaultTextStyle: {
        fontSize: '12px',
        color: '#666',
        cursor: 'pointer',
        marginRight: '8px',
        padding: '4px 0'
      }
    };
  },
  watch: {
    previewVisible(newVal) {
      // 模仿 Modal 的锁屏逻辑
      if (newVal) document.body.style.overflow = 'hidden';
      else document.body.style.overflow = '';
    }
  },
  methods: {
    // 获取当前项应该显示的操作列表
    getVisibleActions(item) {
      const actions = this.config.actions || [];
      return actions.filter(act => {
        if (typeof act.condition === 'function') {
          return act.condition(item);
        }
        return true;
      });
    },
    // 处理操作点击
    handleAction(actConfig, item, index) {
      if (typeof actConfig.action === 'function') {
        actConfig.action(item, index);
      }
    },
    // 打开预览
    handleImageClick(item) {
      if (!item.url) return;
      this.currentPreviewUrl = item.url;
      this.previewVisible = true;
    },
    // 关闭预览
    closePreview() {
      this.previewVisible = false;
      this.currentPreviewUrl = '';
    }
  }
};
</script>

<style scoped>
/* 容器布局：核心响应式逻辑 */
.smart-card-wrapper {
  display: grid;
  /* 自动适应列数：当宽度足够放下多个 cardWidth 时自动换行 */
  grid-template-columns: repeat(auto-fit, minmax(v-bind('config.cardWidth'), 1fr));
  gap: v-bind('config.cardGap');
  width: 100%;
  box-sizing: border-box;
}

/* 单个卡片样式 */
.smart-card-item {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #eee;
  transition: transform 0.2s;
  margin-bottom: 15px;
}

.smart-card-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* 图片区域 */
.image-box {
  position: relative;
  width: 100%;
  height: v-bind('config.imageHeight');
  background-color: #f5f7fa;
  cursor: zoom-in;
  overflow: hidden;
}

.card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.img-mask {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-box:hover .img-mask {
  opacity: 1;
}

.mask-text {
  color: white;
  font-size: 14px;
  font-weight: 500;
}

/* 操作区域 */
.action-box {
  padding: 12px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 40px;
  align-items: center;
  background: #fff;
}

.act-btn:active {
  transform: scale(0.95);
}

/* --- 预览层样式 (模仿 MyModal 风格) --- */
.preview-mask {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.preview-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  max-width: 90%;
  max-height: 90%;
}

.preview-top {
  display: flex;
  border-radius: 20px 20px 0px 0px;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  padding-left: 15px;
  padding-right: 15px;
  width: 100%;
  border-bottom: 1px solid rgb(212 212 212);
  height: 45px;
  box-shadow: 0 0px 5px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
}

.preview-title {
  font-size: 16px;
  font-weight: 500;
  color: #2a2a2a;
}

.preview-close {
  font-weight: 600;
  cursor: pointer;
  font-size: 18px;
}

.preview-body {
  padding: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f9f9f9;
  overflow: auto;
}

.preview-img-full {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 4px;
}
</style>